import '@/styles/main.css'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { loadRuntimeConfig } from '@shared/config/runtime'
import { setTokenProvider } from '@shared/api/auth/tokenProvider'
import { createPasswordTokenProvider } from '@shared/api/auth/passwordTokenProvider'
import { configureHttp } from '@shared/api/http'
import { i18n } from '@shared/i18n'
import { storeRegistryPlugin } from '@shared/stores/resetAllStores'
import { useAuthStore } from '@shared/stores/auth'
import { useUiStore } from '@shared/stores/ui'
import { setStatusAnomalyReporter } from '@shared/domain/status'
import { vCan } from '@shared/directives/vCan'
import { captureError, initObservability } from '@shared/observability'
import { createAppRouter } from '@/router'
import { createAppQueryClient } from './providers/queryClient'
import App from './App.vue'

const useMocks = import.meta.env.VITE_ENABLE_MSW === 'true'

async function bootstrap() {
  const config = await loadRuntimeConfig()

  // Mocks are compiled out of production builds (static env flag).
  if (useMocks) {
    const [{ startMockWorker }, { createMockTokenProvider }] = await Promise.all([
      import('@mocks/browser'),
      import('@shared/api/auth/mockTokenProvider'),
    ])
    await startMockWorker()
    setTokenProvider(createMockTokenProvider())
  } else if (config.auth.mode === 'oidc') {
    if (!config.oidc) throw new Error('config.json: auth.mode "oidc" requires an "oidc" block')
    const { createOidcTokenProvider } = await import('@shared/api/auth/oidcTokenProvider')
    setTokenProvider(createOidcTokenProvider(config.oidc))
  } else {
    setTokenProvider(createPasswordTokenProvider())
  }

  const app = createApp(App)
  const pinia = createPinia()
  pinia.use(storeRegistryPlugin)
  app.use(pinia)
  app.use(i18n)

  const router = createAppRouter()
  configureHttp({
    getLocale: () => useUiStore().locale,
    onSessionExpired: () => {
      useAuthStore().expire()
      void router.replace({
        name: 'login',
        query: { redirect: router.currentRoute.value.fullPath },
      })
    },
  })
  setStatusAnomalyReporter((a) => captureError(new Error(`status ${a.kind}`), { ...a }))

  app.use(router)
  app.use(VueQueryPlugin, { queryClient: createAppQueryClient() })
  app.directive('can', vCan)
  app.config.errorHandler = (err) => captureError(err)

  void initObservability(app, router, config.sentryDsn, config.environment)

  await router.isReady()
  app.mount('#app')
}

bootstrap().catch((e: unknown) => {
  console.error(e)
  const el = document.getElementById('app')
  if (el) el.dataset.bootError = 'true'
})
